import { computed, makeObservable } from 'mobx'
import moment from 'moment'
import type { $Values } from 'utility-types'

import { dateToString } from 'modules/core/lib/dates'
import { Model } from 'modules/core/lib/mobx-rest'

export const FAMILIES = {
  free: 'free',
  hr: 'hr',
  essentials: 'essentials',
  premium: 'premium',
  enterprise: 'enterprise',
  payroll: 'payroll',
}
// Plans that are for sale on the app and public page
export const AVAILABLE_FAMILIES = {
  [FAMILIES.essentials]: FAMILIES.essentials,
  [FAMILIES.premium]: FAMILIES.premium,
}

const INFERIOR_PLANS = {
  [FAMILIES.free]: [],
  [FAMILIES.hr]: [FAMILIES.essentials],
  [FAMILIES.essentials]: [FAMILIES.hr],
  [FAMILIES.premium]: [FAMILIES.hr, FAMILIES.essentials],
  [FAMILIES.payroll]: [FAMILIES.hr, FAMILIES.essentials, FAMILIES.premium],
  [FAMILIES.enterprise]: [FAMILIES.hr, FAMILIES.essentials, FAMILIES.premium],
}

export const UPGRADE_PLANS = {
  [FAMILIES.free]: FAMILIES.essentials,
  [FAMILIES.hr]: FAMILIES.premium,
  [FAMILIES.essentials]: FAMILIES.premium,
  [FAMILIES.premium]: null,
  [FAMILIES.payroll]: null,
  [FAMILIES.enterprise]: null,
}

const INTERVALS = {
  month: 'month',
  year: 'year',
}

export const TIER = {
  business: 'business',
  enterprise: 'enterprise',
  certification: 'certification',
  legacy: 'legacy',
} as const

export const SOLUTIONS = {
  coreTalent: 'coreTalent',
  coreTime: 'coreTime',
  completeBundle: 'completeBundle',
}

export type Families = $Values<typeof FAMILIES>
export type AvailableFamily = $Values<typeof AVAILABLE_FAMILIES>
export type IntervalsType = $Values<typeof INTERVALS>

type CurrenyPartType = 'integer' | 'currency' | 'literal'

export interface CurrencyPart {
  type: CurrenyPartType
  value: string
}

interface IPlan {
  [key: string]: any
}

class Plan extends Model<IPlan> {
  static urlRoot() {
    return '/billing/plans'
  }

  static FAMILIES = FAMILIES
  static AVAILABLE_FAMILIES = AVAILABLE_FAMILIES
  static intervals = INTERVALS
  static DEFAULT_FREE_INTERVAL = INTERVALS.year

  constructor(attr: IPlan = {}) {
    super(attr)
    makeObservable(this, {
      family: computed,
      inferiorPlans: computed,
      coupon: computed,
      formatedDiscountRedeemDate: computed,
      formatedDiscountDurationDate: computed,
      currencyFormatter: computed,
      type: computed,
      isYearly: computed,
      interval: computed,
    })
  }

  get family(): Families {
    return this.attributes.family
  }

  get inferiorPlans(): Families[] {
    return INFERIOR_PLANS[this.family] || []
  }

  get monthlyPriceForOneSeat(): number {
    return this.priceForAMonthInCents / 100
  }

  get priceForAMonthInCents(): number {
    return this.isYearly ? this.priceInCents / 12 : this.priceInCents
  }

  priceForNumberOfSeats(seats: number): number {
    return this.monthlyPriceForOneSeat * seats
  }

  get monthlyPriceWithDiscountForOneSeat(): number {
    return this.priceWithDiscountForAMonthInCents / 100
  }

  get priceWithDiscountForAMonthInCents(): number {
    return this.isYearly ? this.priceWithDiscountInCents / 12 : this.priceWithDiscountInCents
  }

  priceWithDiscountForNumberOfSeats(seats: number): number {
    return this.monthlyPriceWithDiscountForOneSeat * seats
  }

  discountForNumberOfSeats(seats: number): number {
    if (this.validDiscount) {
      return this.priceWithDiscountForNumberOfSeats(seats)
    }

    return 0
  }

  get validDiscount(): boolean {
    if (this.has('price_with_discount_in_cents') && this.has('redeem_by') && this.has('coupon')) {
      return true
    }

    return false
  }

  get coupon(): string | null | undefined {
    if (this.validDiscount) {
      return this.attributes.coupon
    }

    return null
  }

  get formatedDiscountRedeemDate(): string | null | undefined {
    if (this.validDiscount) {
      return dateToString(moment.unix(this.attributes.redeem_by).utc(), 'ui')
    }
  }

  get formatedDiscountDurationDate(): string | null | undefined {
    if (this.validDiscount) {
      const now = moment()

      const duration = dateToString(now.add(this.attributes.duration_in_months, 'months'), 'ui')

      return duration
    }
  }

  get priceWithDiscountInCents(): number {
    return this.attributes.price_with_discount_in_cents
  }

  get priceInCents(): number {
    return this.attributes.price_in_cents
  }

  priceWithCurrencyInParts(price: number) {
    return this.currencyFormatter.formatToParts(price)
  }

  priceWithCurrency(price: number): string {
    return this.currencyFormatter.format(price)
  }

  priceAndCurrencyWithPosition(price: number): Record<string, any> {
    const priceParts = this.priceWithCurrencyInParts(price)
    const currencyFirst = priceParts[0]?.type === 'currency'

    const currencyText = priceParts.find((part) => part.type === 'currency')?.value

    const priceText = priceParts
      .filter((part) => part.type !== 'currency')
      .map((part) => part.value)
      .join('')
      .trim()

    return {
      currencyFirst,
      currencyText,
      priceText,
    }
  }

  get currencyFormatter() {
    return new Intl.NumberFormat(this.attributes.currency_format_locale, {
      style: 'currency',
      currency: this.attributes.currency,
      minimumFractionDigits: 0,
    })
  }

  get type(): string {
    return this.attributes.id.split('-')[0]
  }

  get isYearly(): boolean {
    return this.attributes.interval === INTERVALS.year
  }

  get interval(): string {
    return this.attributes.interval || Plan.DEFAULT_FREE_INTERVAL
  }

  get isLegacy(): boolean {
    const family = this.attributes.family
    const { free, hr, essentials, premium, payroll, enterprise } = FAMILIES

    return [free, hr, essentials, premium, payroll, enterprise].includes(family)
  }

  get tier(): keyof typeof TIER {
    // TODO we should change it as soon as posible because is very fragile now
    // this should be pushed to the backend
    const family = this.attributes.family

    if (family.includes(TIER.business)) {
      return TIER.business
    } else if (family.includes(TIER.enterprise)) {
      return TIER.enterprise
    } else if (family.includes(TIER.certification)) {
      return TIER.certification
    } else if (family.includes('core_')) {
      return TIER.legacy
    } else {
      throw Error(`Inexistent tier for family  ${family}`)
    }
  }

  get solutions(): string {
    // TODO this is a hack because we need it in production today
    // we should change it as soon as posible because is very fragile now
    if (this.attributes.family.includes('talent_only')) {
      return SOLUTIONS.coreTalent
    } else if (
      this.attributes.family.includes('talent') ||
      this.attributes.family.includes('certification')
    ) {
      return SOLUTIONS.completeBundle
    } else {
      return SOLUTIONS.coreTime
    }
  }
}

export default Plan
